import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { ArrowLeft, Users, TrendingUp, Calendar, Search, Download, Award, BarChart3, Clock, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';

const StaffPerformanceDashboard = ({ isAdmin = false, sectionName = null }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { section: urlSection } = useParams();
  
  // Determine the current section - from props, URL params, or null for admin
  const currentSection = sectionName || urlSection?.toUpperCase() || null;
  
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(searchParams.get('staff') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [period, setPeriod] = useState('monthly');
  const [sectionFilter, setSectionFilter] = useState(currentSection || '');
  const [isLoading, setIsLoading] = useState(false);
  
  const [performanceData, setPerformanceData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  // For section view, always filter by that section
  const effectiveSection = isAdmin ? sectionFilter : currentSection;

  // Load staff list
  useEffect(() => {
    loadStaffList();
  }, [effectiveSection]);

  // Load performance data when staff or period changes
  useEffect(() => {
    if (selectedStaff) {
      loadPerformanceData();
      loadChartData();
    }
  }, [selectedStaff, period]);

  // Load summary and top performers
  useEffect(() => {
    loadSummaryData();
    loadTopPerformers();
  }, [period, effectiveSection]);

  const loadStaffList = async () => {
    try {
      const params = {};
      if (effectiveSection) params.section = effectiveSection;
      
      const response = await apiClient.get('/staff/list', { params });
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error('Error loading staff list:', error);
      toast.error('Failed to load staff list');
    }
  };

  const loadPerformanceData = async () => {
    if (!selectedStaff) return;
    
    setIsLoading(true);
    try {
      const params = { period };
      if (effectiveSection) params.section = effectiveSection;
      
      const response = await apiClient.get(`/staff/performance/${encodeURIComponent(selectedStaff)}`, {
        params
      });
      setPerformanceData(response.data);
    } catch (error) {
      console.error('Error loading performance data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChartData = async () => {
    if (!selectedStaff) return;
    
    try {
      const params = { period };
      if (effectiveSection) params.section = effectiveSection;
      
      const response = await apiClient.get(`/staff/chart-data/${encodeURIComponent(selectedStaff)}`, {
        params
      });
      setChartData(response.data.chart_data || []);
    } catch (error) {
      console.error('Error loading chart data:', error);
    }
  };

  const loadSummaryData = async () => {
    try {
      const params = { period };
      if (effectiveSection) params.section = effectiveSection;
      
      const response = await apiClient.get('/staff/performance-summary', { params });
      setSummaryData(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadTopPerformers = async () => {
    try {
      const params = { period, limit: 5 };
      if (effectiveSection) params.section = effectiveSection;
      
      const response = await apiClient.get('/staff/top-performers', { params });
      setTopPerformers(response.data.top_performers || []);
    } catch (error) {
      console.error('Error loading top performers:', error);
    }
  };

  // Filter staff list based on search
  const filteredStaff = useMemo(() => {
    if (!searchQuery) return staffList;
    const query = searchQuery.toLowerCase();
    return staffList.filter(s => 
      s.staff_id.toLowerCase().includes(query) ||
      s.staff_name.toLowerCase().includes(query) ||
      (s.designation || '').toLowerCase().includes(query)
    );
  }, [staffList, searchQuery]);

  const handleStaffSelect = (staffId) => {
    setSelectedStaff(staffId);
    setSearchParams({ staff: staffId });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN');
    } catch {
      return dateStr;
    }
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'daily': return 'Today';
      case 'weekly': return 'Last 7 Days';
      case 'monthly': return 'Last 30 Days';
      case 'yearly': return 'Last 12 Months';
      default: return period;
    }
  };

  const getBackPath = () => {
    if (isAdmin) return '/admin/dashboard';
    if (currentSection) return `/sections/${currentSection.toLowerCase()}`;
    return '/home';
  };

  const getPageTitle = () => {
    if (isAdmin) return 'Staff Performance Dashboard';
    if (currentSection) return `${currentSection} Section - Staff Performance`;
    return 'Staff Performance';
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(getBackPath())}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">{getPageTitle()}</h1>
              <p className="text-sm text-[#E8DCC8]">
                {isAdmin ? 'Track productivity across all sections' : `Track ${currentSection} section productivity`}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Filters Row */}
        <Card className="border-2 border-[#E5DFD0]">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Staff Search & Select */}
              <div className="flex-1">
                <label className="text-sm font-medium text-[#5A5F6E] mb-1 block">Select Staff</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5F6E]" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by ID, name, or designation..."
                    className="pl-10 border-[#E5DFD0]"
                    data-testid="staff-search"
                  />
                </div>
                {searchQuery && filteredStaff.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-w-md bg-white border border-[#E5DFD0] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredStaff.slice(0, 10).map((staff) => (
                      <div
                        key={staff.staff_id}
                        onClick={() => {
                          handleStaffSelect(staff.staff_id);
                          setSearchQuery('');
                        }}
                        className="px-4 py-2 hover:bg-[#F5F1E8] cursor-pointer"
                      >
                        <p className="font-medium text-[#1A1F2E]">{staff.staff_name}</p>
                        <p className="text-xs text-[#5A5F6E]">{staff.staff_id} · {staff.designation || 'Staff'} · {staff.section || '-'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Staff Dropdown (alternative) */}
              <div className="w-full md:w-64">
                <label className="text-sm font-medium text-[#5A5F6E] mb-1 block">Or Select from List</label>
                <Select value={selectedStaff || undefined} onValueChange={handleStaffSelect}>
                  <SelectTrigger data-testid="staff-dropdown">
                    <SelectValue placeholder="Choose staff..." />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.staff_id} value={staff.staff_id}>
                        {staff.staff_name} ({staff.staff_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Period Filter */}
              <div className="w-full md:w-48">
                <label className="text-sm font-medium text-[#5A5F6E] mb-1 block">Time Period</label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger data-testid="period-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Today</SelectItem>
                    <SelectItem value="weekly">Last 7 Days</SelectItem>
                    <SelectItem value="monthly">Last 30 Days</SelectItem>
                    <SelectItem value="yearly">Last 12 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Section Filter (Admin only) */}
              {isAdmin && (
                <div className="w-full md:w-48">
                  <label className="text-sm font-medium text-[#5A5F6E] mb-1 block">Section</label>
                  <Select value={sectionFilter || "all"} onValueChange={(val) => setSectionFilter(val === "all" ? "" : val)}>
                    <SelectTrigger data-testid="section-filter">
                      <SelectValue placeholder="All Sections" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sections</SelectItem>
                      <SelectItem value="FOB">FOB</SelectItem>
                      <SelectItem value="Yarn">Yarn</SelectItem>
                      <SelectItem value="Greige">Greige</SelectItem>
                      <SelectItem value="Processing">Processing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards (when no staff selected) */}
        {!selectedStaff && summaryData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-2 border-[#E5DFD0] bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5A5F6E]">Total Staff</p>
                    <p className="text-2xl font-bold text-[#1A1F2E]">{summaryData.totals?.total_staff || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-[#E5DFD0] bg-gradient-to-br from-green-50 to-white">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5A5F6E]">Active Staff ({getPeriodLabel()})</p>
                    <p className="text-2xl font-bold text-[#1A1F2E]">{summaryData.totals?.active_staff || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-[#E5DFD0] bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-purple-500 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5A5F6E]">Total Scans ({getPeriodLabel()})</p>
                    <p className="text-2xl font-bold text-[#1A1F2E]">{summaryData.totals?.total_scans || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top Performers & Staff List */}
        {!selectedStaff && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Performers */}
            <Card className="border-2 border-[#E5DFD0]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Top Performers
                </CardTitle>
                <CardDescription>{getPeriodLabel()}</CardDescription>
              </CardHeader>
              <CardContent>
                {topPerformers.length > 0 ? (
                  <div className="space-y-3">
                    {topPerformers.map((performer, index) => (
                      <div 
                        key={performer.staff_id}
                        onClick={() => handleStaffSelect(performer.staff_id)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F5F1E8] cursor-pointer"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-[#5A5F6E]'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1A1F2E] truncate">{performer.staff_name}</p>
                          <p className="text-xs text-[#5A5F6E]">{performer.section || '-'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#2D5F4F]">{performer.total_scans}</p>
                          <p className="text-xs text-[#5A5F6E]">scans</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#5A5F6E] py-4">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Staff List */}
            <Card className="border-2 border-[#E5DFD0] lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-[#2D5F4F]" />
                  All Staff Performance
                </CardTitle>
                <CardDescription>Click to view detailed performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E5DFD0]">
                        <th className="text-left py-2 px-3 text-sm font-medium text-[#5A5F6E]">Staff</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-[#5A5F6E]">Section</th>
                        <th className="text-right py-2 px-3 text-sm font-medium text-[#5A5F6E]">Scans</th>
                        <th className="text-right py-2 px-3 text-sm font-medium text-[#5A5F6E]">Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summaryData?.staff_summary || []).slice(0, 15).map((staff) => (
                        <tr 
                          key={staff.staff_id}
                          onClick={() => handleStaffSelect(staff.staff_id)}
                          className="border-b border-[#E5DFD0]/50 hover:bg-[#F5F1E8] cursor-pointer"
                        >
                          <td className="py-2 px-3">
                            <p className="font-medium text-[#1A1F2E]">{staff.staff_name}</p>
                            <p className="text-xs text-[#5A5F6E]">{staff.staff_id}</p>
                          </td>
                          <td className="py-2 px-3 text-sm text-[#5A5F6E]">{staff.section || '-'}</td>
                          <td className="py-2 px-3 text-right font-medium text-[#1A1F2E]">{staff.total_scans}</td>
                          <td className="py-2 px-3 text-right text-sm text-[#5A5F6E]">{staff.submitted_scans}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Individual Staff Performance */}
        {selectedStaff && performanceData && (
          <>
            {/* Staff Info & Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Staff Card */}
              <Card className="border-2 border-[#2D5F4F] md:col-span-1">
                <CardContent className="py-4">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-[#2D5F4F] flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl font-bold text-white">
                        {performanceData.staff?.staff_name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <h3 className="font-bold text-lg text-[#1A1F2E]">{performanceData.staff?.staff_name}</h3>
                    <p className="text-sm text-[#5A5F6E]">{performanceData.staff?.staff_id}</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p className="text-[#5A5F6E]">{performanceData.staff?.designation || 'Staff'}</p>
                      <p className="text-[#2D5F4F] font-medium">{performanceData.staff?.section || '-'}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        setSelectedStaff('');
                        setSearchParams({});
                      }}
                    >
                      Clear Selection
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Metrics */}
              <Card className="border-2 border-[#E5DFD0] bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <Target className="w-10 h-10 text-blue-500" />
                    <div>
                      <p className="text-sm text-[#5A5F6E]">Total Scans</p>
                      <p className="text-3xl font-bold text-[#1A1F2E]">{performanceData.metrics?.total_scans || 0}</p>
                      <p className="text-xs text-[#5A5F6E]">{getPeriodLabel()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#E5DFD0] bg-gradient-to-br from-green-50 to-white">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-10 h-10 text-green-500" />
                    <div>
                      <p className="text-sm text-[#5A5F6E]">Avg Daily</p>
                      <p className="text-3xl font-bold text-[#1A1F2E]">{performanceData.metrics?.avg_daily_scans || 0}</p>
                      <p className="text-xs text-[#5A5F6E]">scans/day</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#E5DFD0] bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-10 h-10 text-purple-500" />
                    <div>
                      <p className="text-sm text-[#5A5F6E]">Days Active</p>
                      <p className="text-3xl font-bold text-[#1A1F2E]">{performanceData.metrics?.days_active || 0}</p>
                      <p className="text-xs text-[#5A5F6E]">{getPeriodLabel()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <Card className="border-2 border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#2D5F4F]" />
                  Performance Trend
                </CardTitle>
                <CardDescription>
                  {period === 'weekly' ? 'Daily scans for last 7 days' :
                   period === 'monthly' ? 'Daily scans for last 30 days' :
                   period === 'yearly' ? 'Monthly scans for last 12 months' : 'Performance over time'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="bar" className="w-full">
                  <TabsList className="mb-4">
                    <TabsTrigger value="bar">Bar Chart</TabsTrigger>
                    <TabsTrigger value="line">Line Chart</TabsTrigger>
                    <TabsTrigger value="area">Area Chart</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="bar">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD0" />
                          <XAxis dataKey="label" stroke="#5A5F6E" fontSize={12} />
                          <YAxis stroke="#5A5F6E" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#FFFBF5', 
                              border: '1px solid #E5DFD0',
                              borderRadius: '8px'
                            }}
                          />
                          <Legend />
                          <Bar dataKey="scans" fill="#2D5F4F" name="Total Scans" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="submitted" fill="#82ca9d" name="Submitted" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="line">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD0" />
                          <XAxis dataKey="label" stroke="#5A5F6E" fontSize={12} />
                          <YAxis stroke="#5A5F6E" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#FFFBF5', 
                              border: '1px solid #E5DFD0',
                              borderRadius: '8px'
                            }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="scans" stroke="#2D5F4F" strokeWidth={2} name="Total Scans" dot={{ fill: '#2D5F4F' }} />
                          <Line type="monotone" dataKey="submitted" stroke="#82ca9d" strokeWidth={2} name="Submitted" dot={{ fill: '#82ca9d' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="area">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD0" />
                          <XAxis dataKey="label" stroke="#5A5F6E" fontSize={12} />
                          <YAxis stroke="#5A5F6E" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#FFFBF5', 
                              border: '1px solid #E5DFD0',
                              borderRadius: '8px'
                            }}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="scans" fill="#2D5F4F" fillOpacity={0.3} stroke="#2D5F4F" name="Total Scans" />
                          <Area type="monotone" dataKey="submitted" fill="#82ca9d" fillOpacity={0.3} stroke="#82ca9d" name="Submitted" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Daily Breakdown & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Breakdown Table */}
              <Card className="border-2 border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Daily Breakdown</CardTitle>
                  <CardDescription>Scan count by date</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="max-h-80 overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b border-[#E5DFD0]">
                          <th className="text-left py-2 px-3 text-sm font-medium text-[#5A5F6E]">Date</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-[#5A5F6E]">Scans</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(performanceData.daily_breakdown || []).slice().reverse().map((day) => (
                          <tr key={day.date} className="border-b border-[#E5DFD0]/50">
                            <td className="py-2 px-3 text-sm text-[#1A1F2E]">{formatDate(day.date)}</td>
                            <td className="py-2 px-3 text-right font-medium text-[#2D5F4F]">{day.count}</td>
                          </tr>
                        ))}
                        {(!performanceData.daily_breakdown || performanceData.daily_breakdown.length === 0) && (
                          <tr>
                            <td colSpan={2} className="py-4 text-center text-[#5A5F6E]">No data available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Submissions */}
              <Card className="border-2 border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Recent Submissions</CardTitle>
                  <CardDescription>Latest work sessions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="max-h-80 overflow-y-auto space-y-3">
                    {(performanceData.submissions || []).map((sub) => (
                      <div key={sub.submission_id} className="p-3 bg-[#F5F1E8] rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-[#1A1F2E]">{sub.scan_count} scans</p>
                            <p className="text-xs text-[#5A5F6E]">{sub.submission_id}</p>
                          </div>
                          <p className="text-xs text-[#5A5F6E]">{formatDate(sub.submitted_at)}</p>
                        </div>
                      </div>
                    ))}
                    {(!performanceData.submissions || performanceData.submissions.length === 0) && (
                      <p className="text-center text-[#5A5F6E] py-4">No submissions yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Empty State */}
        {!selectedStaff && staffList.length === 0 && (
          <Card className="border-2 border-dashed border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto text-[#E5DFD0] mb-4" />
              <p className="text-[#5A5F6E]">No staff records found</p>
              <p className="text-sm text-[#5A5F6E]/70 mt-1">Import staff data to get started</p>
              {isAdmin && (
                <Button
                  className="mt-4 bg-[#2D5F4F] hover:bg-[#234A3D]"
                  onClick={() => navigate('/admin/import')}
                >
                  Import Staff
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default StaffPerformanceDashboard;
